import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Special service within the audit trail component to facilitate the translation of values.
 *
 * The backend simply returns (ENUM) values, as defined in the building-characteristics-blueprints.ts. However,
 * there's no clear mechanism to translate these enums into a i18n. Sometimes the values from the i18n/energy-label files
 * is used, sometimes from the i18n/<root>. Thus, different keys, different files.
 *
 * As long as there's no clean way to translate audit trail values, we use this service which does some 'magic' and iterates
 * through a subset of translation keys and does a 'best effort' translation matching...
 */
@Injectable({
  providedIn: 'root'
})
export class AuditTrailTranslateService {
  private readonly translateService = inject(TranslateService);

  private readonly auditTrailValueKeys: string[];

  constructor() {
    this.auditTrailValueKeys = this.getKeys(
      this.translateService.store.translations[this.translateService.currentLang]
    );
  }

  public findTranslation(enumValue: string): string {
    for (let auditTrailValueKey of this.auditTrailValueKeys) {
      const splittedKeyElements = auditTrailValueKey.split('.');
      const lastKeyElement = splittedKeyElements[splittedKeyElements.length - 1];
      if (lastKeyElement === enumValue) {
        return this.translateService.instant(auditTrailValueKey);
      }
    }

    return enumValue;
  }

  private getKeys(object: Object, prefix: string = ''): string[] {
    return Object.keys(object).reduce<string[]>((keys, element) => {
      if (typeof object[element] === 'object') {
        keys = keys.concat(this.getKeys(object[element], `${prefix}${element}.`));
      } else {
        // we ONLY need a few dedicated keys
        const key = prefix ? `${prefix}${element}` : element;
        if (key.startsWith('building.characteristics.') || key.startsWith('heatingConsumptionType')) {
          keys.push(key);
        }
      }
      return keys;
    }, []);
  }
}
